import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { Booking, CreateBookingDto, Availability, BookingStatus } from '../types';
import { bookingsApi } from '../services/bookingApi';
import { getErrorMessage } from '../utils/errorUtils';

interface BookingState {
  bookings: Booking[];
  currentBooking: Booking | null;
  availability: Availability[];
  isLoading: boolean;
  error: string | null;
  isEligible: boolean;
}

type BookingAction =
  | { type: 'BOOKING_START' }
  | { type: 'BOOKING_SUCCESS'; payload: Booking[] }
  | { type: 'BOOKING_ERROR'; payload: string }
  | { type: 'SET_CURRENT_BOOKING'; payload: Booking | null }
  | { type: 'ADD_BOOKING'; payload: Booking }
  | { type: 'UPDATE_BOOKING'; payload: Booking }
  | { type: 'SET_AVAILABILITY'; payload: Availability[] }
  | { type: 'SET_ELIGIBILITY'; payload: boolean }
  | { type: 'CLEAR_ERROR' };

const initialState: BookingState = {
  bookings: [],
  currentBooking: null,
  availability: [],
  isLoading: false,
  error: null,
  isEligible: false,
};

const bookingReducer = (state: BookingState, action: BookingAction): BookingState => {
  switch (action.type) {
    case 'BOOKING_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'BOOKING_SUCCESS':
      return {
        ...state,
        bookings: action.payload,
        isLoading: false,
        error: null,
      };
    case 'BOOKING_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case 'SET_CURRENT_BOOKING':
      return {
        ...state,
        currentBooking: action.payload,
      };
    case 'ADD_BOOKING':
      return {
        ...state,
        bookings: [action.payload, ...state.bookings],
      };
    case 'UPDATE_BOOKING':
      return {
        ...state,
        bookings: state.bookings.map(b => 
          b.id === action.payload.id ? action.payload : b
        ),
        currentBooking: state.currentBooking?.id === action.payload.id 
          ? action.payload : state.currentBooking,
      };
    case 'SET_AVAILABILITY':
      return {
        ...state,
        availability: action.payload,
      };
    case 'SET_ELIGIBILITY':
      return {
        ...state,
        isEligible: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

interface BookingContextType {
  state: BookingState;
  loadBookings: () => Promise<void>;
  createBooking: (bookingData: CreateBookingDto) => Promise<Booking>;
  getBooking: (bookingId: number) => Promise<void>;
  cancelBooking: (bookingId: number, reason?: string) => Promise<void>;
  loadAvailability: (barbershopId: number, barberId: number, date: string) => Promise<void>;
  checkEligibility: () => Promise<void>;
  clearError: () => void;
}

export const BookingContext = createContext<BookingContextType | undefined>(undefined);

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};

interface BookingProviderProps {
  children: React.ReactNode;
}

export const BookingProvider: React.FC<BookingProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  const loadBookings = useCallback(async (): Promise<void> => {
    try {
      dispatch({ type: 'BOOKING_START' });
      const bookings = await bookingsApi.getUserBookings();
      dispatch({ type: 'BOOKING_SUCCESS', payload: bookings });
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to load bookings');
      dispatch({ type: 'BOOKING_ERROR', payload: message });
    }
  }, []);

  const createBooking = useCallback(async (bookingData: CreateBookingDto): Promise<Booking> => {
    try {
      dispatch({ type: 'BOOKING_START' });
      const booking = await bookingsApi.createBooking(bookingData);
      dispatch({ type: 'ADD_BOOKING', payload: booking });
      return booking;
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to create booking');
      dispatch({ type: 'BOOKING_ERROR', payload: message });
      throw error;
    }
  }, []);

  const getBooking = useCallback(async (bookingId: number): Promise<void> => {
    try {
      const booking = await bookingsApi.getBooking(bookingId);
      dispatch({ type: 'SET_CURRENT_BOOKING', payload: booking });
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to get booking');
      dispatch({ type: 'BOOKING_ERROR', payload: message });
    }
  }, []);

  const cancelBooking = useCallback(async (bookingId: number, reason?: string): Promise<void> => {
    try {
      await bookingsApi.cancelBooking(bookingId, reason);
      // Update the booking status in state
      const updatedBooking = state.bookings.find(b => b.id === bookingId);
      if (updatedBooking) {
        const cancelledBooking = {
          ...updatedBooking,
          status: BookingStatus.Cancelled,
          cancellationReason: reason,
          cancelledAt: new Date().toISOString(),
        };
        dispatch({ type: 'UPDATE_BOOKING', payload: cancelledBooking });
      }
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to cancel booking');
      dispatch({ type: 'BOOKING_ERROR', payload: message });
      throw error;
    }
  }, [state.bookings]);

  const loadAvailability = useCallback(async (
    barbershopId: number, 
    barberId: number, 
    date: string
  ): Promise<void> => {
    try {
      const availability = await bookingsApi.getAvailability(barbershopId, barberId, date);
      dispatch({ type: 'SET_AVAILABILITY', payload: availability });
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to load availability');
      dispatch({ type: 'BOOKING_ERROR', payload: message });
    }
  }, []);

  const checkEligibility = useCallback(async (): Promise<void> => {
    try {
      const result = await bookingsApi.checkEligibility();
      dispatch({ type: 'SET_ELIGIBILITY', payload: result.isEligible });
    } catch (error) {
      dispatch({ type: 'SET_ELIGIBILITY', payload: false });
    }
  }, []);

  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const value: BookingContextType = {
    state,
    loadBookings,
    createBooking,
    getBooking,
    cancelBooking,
    loadAvailability,
    checkEligibility,
    clearError,
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};